import { useEffect } from 'react';
import { Risco } from '../types';
import { TIPOS_PERIGO } from '../constants';
import { obterPerigosEspecificos } from '../../constants/perigos-especificos';


export function useModalInitialization(
  aberto: boolean,
  parte: string,
  riscos: Risco[],
  descricaoMaquina: string,
  setNomeParte: (nome: string) => void,
  setFotoParte1: (foto: string) => void,
  setFotoParte2: (foto: string) => void,
  setFotoParte3: (foto: string) => void,
  setFotoParte4: (foto: string) => void,
  setCiclosVida: (ciclos: string[]) => void,
  setEventoPerigoso: (evento: string) => void,
  setConforme: (conforme: boolean) => void,
  setTiposPerigoSelecionados: (tipos: string[]) => void,
  setPerigosEncontrados: (perigos: string[]) => void,
  setPotenciaisConsequencias: (consequencias: string[]) => void,
  setPotenciaisConsequenciasEspecificas: (consequencias: string[]) => void,
  setOrigemEspecifica: (origem: string[]) => void,
  setDescricaoCondicaoAtual: (descricao: string[]) => void,
  setMedidasControle: (medidas: string[]) => void,
  setExigenciasNormativas: (exigencias: string[]) => void,
  setAdequacoesNormativas: (adequacoes: string[]) => void,
  setPronta: (pronta: boolean) => void,
  setRiscosEditados: (riscos: Risco[]) => void,
  setDescricaoMaquinaIA: (descricao: string) => void,
  setPartesCorpoAtingidas: (partes: string) => void,
  setExibirHRN: (exibir: boolean) => void,
  setExibirSRPCS: (exibir: boolean) => void
) {
  // Atualizar descrição da máquina quando o modal abrir
  useEffect(() => {
    if (aberto && descricaoMaquina) {
      setDescricaoMaquinaIA(descricaoMaquina);
    }
  }, [aberto, descricaoMaquina, setDescricaoMaquinaIA]);

  useEffect(() => {
    if (aberto) {
      setNomeParte(parte);

      if (riscos.length > 0) {
        const primeiroRisco = riscos[0];
        setFotoParte1(primeiroRisco.fotoAtualUrl1 || '');
        setFotoParte2(primeiroRisco.fotoAtualUrl2 || '');
        setFotoParte3(primeiroRisco.fotoAtualUrl3 || '');
        setFotoParte4(primeiroRisco.fotoAtualUrl4 || '');
        setCiclosVida(primeiroRisco.cicloVida ? primeiroRisco.cicloVida.split(',').map(c => c.trim()) : []);
        setEventoPerigoso(primeiroRisco.eventoPerigoso || '');
        setConforme(primeiroRisco.conformeAtual ?? true);
        setExibirHRN(primeiroRisco.exibirHRN !== false);
        setExibirSRPCS(primeiroRisco.exibirSRPCS !== false);

        // Processar tipos de perigo - pode ser string separada por vírgula ou array
        const tiposPerigo = primeiroRisco.tipoPerigo
          ? (Array.isArray(primeiroRisco.tipoPerigo)
            ? primeiroRisco.tipoPerigo
            : primeiroRisco.tipoPerigo.split(',').map(p => p.trim()).filter(p => p))
          : [];

        // Função auxiliar para verificar se é um tipo principal
        const isTipoPrincipal = (t: string): boolean => {
          if (!t || !t.trim()) return false;
          const tLower = t.toLowerCase().trim();

          // Verificações diretas
          if (tLower === 'mecânico' || tLower === 'mecanico' || tLower === 'mecánico') return true;
          if (tLower === 'elétrico' || tLower === 'eletrico' || tLower === 'eléctrico') return true;
          if (tLower === 'ergonômico' || tLower === 'ergonomico' || tLower === 'ergonómico') return true;

          // Verificações com includes
          return TIPOS_PERIGO.some(tp => {
            const tpLower = tp.toLowerCase();
            return tpLower === tLower ||
              (tLower.includes('mecanico') && (tpLower.includes('mecânico') || tpLower.includes('mecanico'))) ||
              (tLower.includes('eletrico') && (tpLower.includes('elétrico') || tpLower.includes('eletrico'))) ||
              (tLower.includes('ergonomico') && (tpLower.includes('ergonômico') || tpLower.includes('ergonomico')));
          });
        };

        // Filtrar apenas os tipos válidos (Mecânico, Elétrico, Ergonômico)
        const tiposValidos = tiposPerigo
          .filter(t => isTipoPrincipal(t))
          .map(t => {
            // Normalizar nomes
            const tLower = t.toLowerCase();
            if (tLower.includes('mecanico') || tLower.includes('mecânico')) return 'Mecânico';
            if (tLower.includes('eletrico') || tLower.includes('elétrico')) return 'Elétrico';
            if (tLower.includes('ergonomico') || tLower.includes('ergonômico')) return 'Ergonômico';
            return t;
          });
        setTiposPerigoSelecionados(tiposValidos);

        // Obter perigos específicos para normalização de casing
        const tipoPrincipal = tiposValidos[0] || '';
        const perigosEspecificosConst = tipoPrincipal ? obterPerigosEspecificos(tipoPrincipal) : null;

        // Perigos encontrados vêm do campo perigosEncontrados do banco (separado do tipoPerigo)
        const perigosEncontradosDoBanco = primeiroRisco.perigosEncontrados
          ? primeiroRisco.perigosEncontrados.split(',').map((p: string) => p.trim()).filter((p: string) => p && p !== 'teste')
          : [];

        // Remover duplicações (case-insensitive) e normalizar casing
        const perigosEncontradosUnicos = new Set<string>();
        perigosEncontradosDoBanco.forEach((pe: string) => {
          const peTrimmed = pe.trim();
          if (peTrimmed && peTrimmed !== 'teste') {
            const peLower = peTrimmed.toLowerCase();
            // Garantir que não são tipos principais
            if (!isTipoPrincipal(peTrimmed)) {
              // Tentar encontrar canonical (case-insensitive) nos itens específicos
              let valorFinal = peTrimmed;
              if (perigosEspecificosConst) {
                const canonical = perigosEspecificosConst.origem.find(
                  orig => orig.toLowerCase() === peLower
                );
                if (canonical) {
                  valorFinal = canonical;
                }
              }

              // Verificar se já existe na lista final (pelo valor canonical ou original)
              const jaExiste = Array.from(perigosEncontradosUnicos).some(
                existente => existente.toLowerCase() === valorFinal.toLowerCase()
              );

              if (!jaExiste) {
                perigosEncontradosUnicos.add(valorFinal);
              }
            }
          }
        });
        setPerigosEncontrados(Array.from(perigosEncontradosUnicos));

        // Normalizar consequências
        const consequenciasDoBanco = primeiroRisco.consequencias ? primeiroRisco.consequencias.split(',').map(c => c.trim()) : [];
        const consequenciasNormalizadas = consequenciasDoBanco.map(c => {
          if (perigosEspecificosConst) {
            const canonical = perigosEspecificosConst.potenciaisConsequencias.find(
              pc => pc.toLowerCase() === c.toLowerCase()
            );
            return canonical || c;
          }
          return c;
        });
        setPotenciaisConsequencias(consequenciasNormalizadas);

        // Inicializar perigos específicos
        if (primeiroRisco.potenciaisConsequenciasEspecificas) {
          const consequencias = Array.isArray(primeiroRisco.potenciaisConsequenciasEspecificas)
            ? primeiroRisco.potenciaisConsequenciasEspecificas
            : [];
          setPotenciaisConsequenciasEspecificas(consequencias);
        } else {
          setPotenciaisConsequenciasEspecificas([]);
        }

        if (primeiroRisco.origemEspecifica) {
          const origem = Array.isArray(primeiroRisco.origemEspecifica)
            ? primeiroRisco.origemEspecifica
            : [];
          setOrigemEspecifica(origem);
        } else {
          setOrigemEspecifica([]);
        }
        setDescricaoCondicaoAtual(primeiroRisco.adequacoesTecnicas ? primeiroRisco.adequacoesTecnicas.split('\n').filter((l: string) => l.trim()) : []);
        setMedidasControle(primeiroRisco.medidasControle ? primeiroRisco.medidasControle.split('\n').filter((l: string) => l.trim()) : []);
        setExigenciasNormativas(primeiroRisco.exigenciasNormativas ? primeiroRisco.exigenciasNormativas.split('\n').filter((l: string) => l.trim()) : []);
        setAdequacoesNormativas(primeiroRisco.adequacoesNormativas ? primeiroRisco.adequacoesNormativas.split('\n').filter((l: string) => l.trim()) : []);
        setPronta(primeiroRisco.pronta ?? false);
        setPartesCorpoAtingidas(primeiroRisco.partesCorpoAtingidas || 'Corpo inteiro');

        // Inicializar riscos com valores padrão se não existirem (preservar valores decimais pequenos)
        const riscosInicializados = riscos.map(r => ({
          ...r,
          probabilidadeAtual: r.probabilidadeAtual ?? 1,
          frequenciaAtual: r.frequenciaAtual ?? 1,
          gravidadeAtual: r.gravidadeAtual ?? 1,
          exposicaoAtual: r.exposicaoAtual ?? 1,
          probabilidadeFuturo: r.probabilidadeFuturo ?? r.probabilidadeAtual ?? 1,
          frequenciaFuturo: r.frequenciaFuturo ?? r.frequenciaAtual ?? 1,
          gravidadeFuturo: r.gravidadeFuturo ?? r.gravidadeAtual ?? 1,
          exposicaoFuturo: r.exposicaoFuturo ?? r.exposicaoAtual ?? 1,
        }));
        setRiscosEditados(riscosInicializados);
      } else {
        // Parte nova - inicializar com valores padrão
        setFotoParte1('');
        setFotoParte2('');
        setFotoParte3('');
        setFotoParte4('');
        setCiclosVida([]);
        setEventoPerigoso('');
        setConforme(true);
        setTiposPerigoSelecionados([]);
        setPerigosEncontrados([]);
        setPotenciaisConsequencias([]);
        setPotenciaisConsequenciasEspecificas([]);
        setOrigemEspecifica([]);
        setDescricaoCondicaoAtual([]);
        setMedidasControle([]);
        setExigenciasNormativas([]);
        setAdequacoesNormativas([]);
        setPronta(false);
        setPartesCorpoAtingidas('Corpo inteiro');

        // Criar um risco inicial vazio
        setRiscosEditados([{
          numero: 1,
          parte: parte,
          tipoPerigo: 'Mecânico',
          gravidadeAtual: 1,
          frequenciaAtual: 1,
          exposicaoAtual: 1,
          probabilidadeAtual: 1,
          hrnAtual: 1,
        }]);
      }
    }
  }, [aberto, riscos, parte, setNomeParte, setFotoParte1, setFotoParte2, setFotoParte3, setFotoParte4, setCiclosVida, setEventoPerigoso, setConforme, setTiposPerigoSelecionados, setPerigosEncontrados, setPotenciaisConsequencias, setPotenciaisConsequenciasEspecificas, setOrigemEspecifica, setDescricaoCondicaoAtual, setMedidasControle, setExigenciasNormativas, setAdequacoesNormativas, setPronta, setRiscosEditados, setPartesCorpoAtingidas]);
}

